// Shared framer-motion variants + content data for Core Loop Tech

export const easeSmooth = [0.22, 1, 0.36, 1];

export const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: easeSmooth, delay: i * 0.08 },
  }),
};

export const fadeIn = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 1, ease: easeSmooth } },
};

export const staggerParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.1 } },
};

export const lineReveal = {
  hidden: { y: "110%" },
  show: (i = 0) => ({
    y: "0%",
    transition: { duration: 0.9, ease: easeSmooth, delay: 0.15 + i * 0.12 },
  }),
};

// ---------- Site content ----------
export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Contact", to: "/contact" },
];

export const OFFICE = {
  lines: [
    "Unit 711 - 7F, Block A",
    "Focal Industrial Centre",
    "21 Man Lok Street, Hung Hom",
    "Kowloon, Hong Kong",
  ],
  email: "contact@thecorelooptech.com",
  region: "Hong Kong SAR",
};

export const IMAGES = {
  ai: "https://images.unsplash.com/photo-1782330300409-f6c1ae5f64cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGFydGlmaWNpYWwlMjBpbnRlbGxpZ2VuY2UlMjBkYXJrJTIwdGVjaHxlbnwwfHx8fDE3ODUyMjM2ODV8MA&ixlib=rb-4.1.0&q=85",
  crm: "https://images.unsplash.com/photo-1762278804768-7109128de73f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGFydGlmaWNpYWwlMjBpbnRlbGxpZ2VuY2UlMjBkYXJrJTIwdGVjaHxlbnwwfHx8fDE3ODUyMjM2ODV8MA&ixlib=rb-4.1.0&q=85",
  security: "https://images.pexels.com/photos/37730211/pexels-photo-37730211.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  manifesto: "https://images.unsplash.com/photo-1594973782943-3314fe063f68?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxob25nJTIwa29uZyUyMGNpdHlzY2FwZSUyMG5pZ2h0JTIwZGFya3xlbnwwfHx8fDE3ODUyMjM2ODV8MA&ixlib=rb-4.1.0&q=85",
  location: "https://images.unsplash.com/photo-1513622790541-eaa84d356909?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxob25nJTIwa29uZyUyMGNpdHlzY2FwZSUyMG5pZ2h0JTIwZGFya3xlbnwwfHx8fDE3ODUyMjM2ODV8MA&ixlib=rb-4.1.0&q=85",
};

const U = (id, w = 1200) => `https://images.unsplash.com/${id}?crop=entropy&cs=srgb&fm=jpg&q=85&w=${w}`;

// Extra curated dark-tech imagery (verified live)
export const TECH = {
  circuit: U("photo-1518770660439-4636190af475"),
  codeMatrix: U("photo-1526374965328-7f61d4dc18c5"),
  cyberLock: U("photo-1550751827-4bd374c3f58b"),
  networkGlobe: U("photo-1451187580459-43490279c0fa"),
  serverRoom: U("photo-1558494949-ef010cbdcc31"),
  aiAbstract: U("photo-1620712943543-bcc4688e7485"),
  aiChip: U("photo-1633356122544-f134324a6cee"),
  dataViz: U("photo-1504384308090-c894fdcc538d"),
  codeScreen: U("photo-1531746790731-6c087fecd65a"),
  dataCenter: U("photo-1487058792275-0ad4aaf24ca7"),
  laptopCode: U("photo-1544197150-b99a580bb7a8"),
  neural: U("photo-1591453089816-0fbb971b454c"),
};

// Home "in motion" image gallery
export const TECH_GALLERY = [
  { img: TECH.aiChip, label: "Neural inference", tag: "AI" },
  { img: TECH.codeMatrix, label: "Model pipelines", tag: "AI" },
  { img: TECH.dataViz, label: "Live analytics", tag: "CRM" },
  { img: TECH.serverRoom, label: "Secure infra", tag: "Security" },
  { img: TECH.networkGlobe, label: "Global mesh", tag: "Network" },
  { img: TECH.circuit, label: "Edge compute", tag: "Systems" },
];

// AI deep-dive highlights (Home + Services)
export const AI_HIGHLIGHTS = [
  {
    title: "Generative copilots",
    body: "Domain-tuned LLM assistants and RAG systems that read your knowledge base and act inside your workflows.",
    img: TECH.aiAbstract,
  },
  {
    title: "Agentic automation",
    body: "Multi-step AI agents that plan, call tools and complete real tasks — quoting, triage, research and beyond.",
    img: TECH.neural,
  },
  {
    title: "Vision & documents",
    body: "OCR, extraction and computer-vision pipelines that turn PDFs, forms and images into structured data.",
    img: TECH.codeScreen,
  },
  {
    title: "Forecasting & insight",
    body: "Predictive models and anomaly detection that surface what matters before it becomes a problem.",
    img: TECH.dataViz,
  },
];

export const SERVICES = [
  {
    key: "ai",
    index: "01",
    title: "Applied AI Systems",
    tagline: "Intelligence, engineered into the loop.",
    image: IMAGES.ai,
    summary:
      "We design and ship production-grade AI — generative copilots, autonomous agents, retrieval pipelines, forecasting models and computer-vision systems that plug directly into your operations and keep learning.",
    points: [
      "LLM copilots & RAG knowledge assistants",
      "Autonomous, tool-using AI agents",
      "Predictive & forecasting models",
      "Document intelligence & OCR pipelines",
      "Computer vision & image analysis",
      "MLOps, evaluation & guardrails",
    ],
  },
  {
    key: "crm",
    index: "02",
    title: "CRM Engineering",
    tagline: "Every customer signal, in one clean loop.",
    image: IMAGES.crm,
    summary:
      "Custom CRM builds and deep integrations that unify sales, support and marketing data — automation-first, reporting-ready and tailored to how your teams actually work.",
    points: [
      "Custom CRM platforms & portals",
      "Salesforce / HubSpot integrations",
      "Workflow & pipeline automation",
      "Unified analytics dashboards",
    ],
  },
  {
    key: "security",
    index: "03",
    title: "Network Security",
    tagline: "Tactical defense for critical infrastructure.",
    image: IMAGES.security,
    summary:
      "Hardening, monitoring and response for modern networks. We audit, architect and defend — from zero-trust design to 24/7 threat detection across cloud and on-prem.",
    points: [
      "Security audits & penetration testing",
      "Zero-trust network architecture",
      "SIEM, monitoring & incident response",
      "Cloud & endpoint hardening",
    ],
  },
];

export const STATS = [
  { value: "40+", label: "Systems shipped" },
  { value: "12", label: "Industries served" },
  { value: "99.9%", label: "Uptime engineered" },
  { value: "HK", label: "Rooted in Kowloon" },
];

// Broader technology-services catalogue (icon = lucide name)
export const MORE_SERVICES = [
  {
    key: "cloud",
    icon: "Cloud",
    title: "Cloud & DevOps",
    body: "Multi-cloud expertise across AWS, Azure, Google Cloud and Alibaba Cloud — migration, infrastructure-as-code, CI/CD and Kubernetes.",
  },
  {
    key: "financial",
    icon: "Landmark",
    title: "Financial Systems",
    body: "Payment platforms, ledgers, trading and reconciliation systems — engineered for accuracy, auditability and compliance.",
  },
  {
    key: "cms",
    icon: "LayoutTemplate",
    title: "CMS Solutions",
    body: "Headless and traditional CMS builds — WordPress, Strapi, Sanity and custom platforms your team can actually manage.",
  },
  {
    key: "software",
    icon: "Code2",
    title: "Custom Software",
    body: "Bespoke web and mobile applications built for scale, from architecture to launch and beyond.",
  },
  {
    key: "data",
    icon: "BarChart3",
    title: "Data & Analytics",
    body: "Data warehouses, ETL pipelines and BI dashboards that turn raw data into decisions.",
  },
  {
    key: "managed",
    icon: "ServerCog",
    title: "Managed IT",
    body: "Proactive infrastructure monitoring, support and maintenance so your systems never sleep.",
  },
  {
    key: "design",
    icon: "Palette",
    title: "UI/UX & Product",
    body: "Product strategy, interface design and prototyping that make complex software feel effortless.",
  },
  {
    key: "integration",
    icon: "Workflow",
    title: "Integration & APIs",
    body: "Connect your tools — payments, ERPs, third-party APIs — into one seamless, automated stack.",
  },
];

// Layered / defense-in-depth security offering
export const SECURITY_LAYERS = [
  { icon: "Network", title: "Perimeter & Network", body: "Next-gen firewalls, IDS/IPS, VPNs and micro-segmentation to lock down the edge." },
  { icon: "KeyRound", title: "Identity & Access", body: "Zero-trust access, MFA, SSO and least-privilege RBAC across every system." },
  { icon: "Bug", title: "Application Security", body: "Secure SDLC, SAST/DAST scanning and penetration testing on every release." },
  { icon: "Lock", title: "Data Protection", body: "Encryption at rest and in transit, key management, DLP and secure backups." },
  { icon: "Eye", title: "Monitoring & Response", body: "SIEM, 24/7 threat detection and a rehearsed incident-response playbook." },
  { icon: "FileCheck", title: "Compliance & Governance", body: "ISO 27001, GDPR and PCI-aligned controls with continuous auditing." },
];

// Cloud platforms we operate across
export const CLOUD_PROVIDERS = [
  { name: "AWS", note: "EKS · Lambda · RDS" },
  { name: "Microsoft Azure", note: "AKS · Functions · Cosmos DB" },
  { name: "Google Cloud", note: "GKE · BigQuery · Run" },
  { name: "Alibaba Cloud", note: "ACK · OSS · PolarDB" },
];

export const MANIFESTO = [
  {
    no: "01",
    title: "We start with the loop, not the feature.",
    body: "Every business runs on feedback loops — data in, decisions out, action back. We map that loop first, then engineer the technology that makes it faster, tighter and smarter.",
  },
  {
    no: "02",
    title: "Systems over screens.",
    body: "A beautiful interface means nothing without the architecture underneath. We build resilient backends, secure networks and clean data models that outlast trends.",
  },
  {
    no: "03",
    title: "Ship, measure, refine — repeat.",
    body: "We deploy small, learn fast and iterate relentlessly. Nothing we build is static; it evolves with your business through disciplined, measurable loops.",
  },
];
